/**
 * Clip glyph line segments against the wall bodies of a wall sketch — used by
 * beam glyphs so their dashed outline visually STOPS at the wall face instead
 * of drawing across the wall fill (the beam's endpoints sit on wall
 * CENTERLINES, so the raw rectangle would overlap half a wall at each end).
 *
 * Each wall edge contributes an oriented band (centerline ± left/right face
 * sizes over the edge span); curved edges are tessellated into short straight
 * bands. Every glyph segment keeps only the parts OUTSIDE all bands.
 */

import { tessellateEdge } from '../../utils/skeleton/arcGeometry';
import type { PointoramaWallSketch } from '../../utils/PointoramaWallSketch';

/** Oriented wall band: origin + unit direction + span + face offsets. */
interface WallBand {
  ox: number;
  oy: number;
  ux: number;
  uy: number;
  len: number;
  /** Face size on the +90°-CCW side of the direction. */
  left: number;
  /** Face size on the −90° side. */
  right: number;
}

function collectWallBands(wallSketch: PointoramaWallSketch): WallBand[] {
  const bands: WallBand[] = [];
  for (const edge of wallSketch.getEdges()) {
    const a = wallSketch.getNode(edge.startNodeId);
    const b = wallSketch.getNode(edge.endNodeId);
    if (!a || !b) continue;
    const left = edge.leftSize ?? 0.1;
    const right = edge.rightSize ?? 0.1;
    // Curved edges become short straight bands along the tessellated arc.
    const points = tessellateEdge({ x: a.position.x, y: a.position.y }, { x: b.position.x, y: b.position.y }, edge.bulge);
    for (let i = 0; i + 1 < points.length; i++) {
      const dx = points[i + 1].x - points[i].x;
      const dy = points[i + 1].y - points[i].y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) continue;
      bands.push({ ox: points[i].x, oy: points[i].y, ux: dx / len, uy: dy / len, len, left, right });
    }
  }
  return bands;
}

/** The t-interval of segment a→b lying INSIDE `band`, or null. Liang-Barsky
 *  over the band's two slabs (along-span and across-thickness). */
function segmentBandInterval(ax: number, ay: number, bx: number, by: number, band: WallBand): [number, number] | null {
  let t0 = 0;
  let t1 = 1;
  // Each slab: lo ≤ f(t) ≤ hi with f linear in t.
  const slabs: Array<{ f0: number; f1: number; lo: number; hi: number }> = [
    {
      f0: (ax - band.ox) * band.ux + (ay - band.oy) * band.uy,
      f1: (bx - band.ox) * band.ux + (by - band.oy) * band.uy,
      lo: 0,
      hi: band.len,
    },
    {
      // Signed distance toward the +90°-CCW (left) side.
      f0: -(ax - band.ox) * band.uy + (ay - band.oy) * band.ux,
      f1: -(bx - band.ox) * band.uy + (by - band.oy) * band.ux,
      lo: -band.right,
      hi: band.left,
    },
  ];
  for (const { f0, f1, lo, hi } of slabs) {
    const df = f1 - f0;
    if (Math.abs(df) < 1e-12) {
      if (f0 < lo || f0 > hi) return null;
      continue;
    }
    let ta = (lo - f0) / df;
    let tb = (hi - f0) / df;
    if (ta > tb) [ta, tb] = [tb, ta];
    t0 = Math.max(t0, ta);
    t1 = Math.min(t1, tb);
    if (t0 >= t1) return null;
  }
  return [t0, t1];
}

/** Subtract `cut` from every interval in `kept` (all within [0,1]). */
function subtractInterval(kept: Array<[number, number]>, cut: [number, number]): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const [k0, k1] of kept) {
    if (cut[1] <= k0 || cut[0] >= k1) {
      out.push([k0, k1]);
      continue;
    }
    if (cut[0] > k0) out.push([k0, cut[0]]);
    if (cut[1] < k1) out.push([cut[1], k1]);
  }
  return out;
}

/**
 * Return `segments` (flat `[x,y,z, x,y,z, …]`, every 6 numbers one segment)
 * with all parts inside the sketch's wall bodies removed.
 */
export function clipSegmentsOutsideWalls(segments: number[], wallSketch: PointoramaWallSketch): number[] {
  const bands = collectWallBands(wallSketch);
  if (bands.length === 0) return segments;

  const out: number[] = [];
  for (let i = 0; i + 5 < segments.length; i += 6) {
    const ax = segments[i];
    const ay = segments[i + 1];
    const z = segments[i + 2];
    const bx = segments[i + 3];
    const by = segments[i + 4];
    let kept: Array<[number, number]> = [[0, 1]];
    for (const band of bands) {
      const inside = segmentBandInterval(ax, ay, bx, by, band);
      if (!inside) continue;
      kept = subtractInterval(kept, inside);
      if (kept.length === 0) break;
    }
    for (const [t0, t1] of kept) {
      if (t1 - t0 < 1e-4) continue;
      out.push(ax + (bx - ax) * t0, ay + (by - ay) * t0, z, ax + (bx - ax) * t1, ay + (by - ay) * t1, z);
    }
  }
  return out;
}
